import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import myAxios from '../AxiosUrl';
import styles from './AICalling.module.css';

/**
 * AI Calling → Calls: this month in numbers and every call (newest first) — order confirmation,
 * marketing, incoming (AI), softphone, tests — filterable, with the conversation and the
 * recording of each call (CallDetail is also used by the Order List and the campaign page).
 */

const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const taka = (n) => `৳${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const dur = (s) => (s > 0 ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}` : '—');

const KIND = { order: 'Order confirmation', marketing: 'Marketing', support: 'Incoming (AI)', softphone: 'Softphone', test: 'Test call' };
const ORDER_RESULT = {
    confirmed: ['Confirmed', styles.pillGreen],
    change: ['Change asked', styles.pillAmber],
    cancelled: ['Cancelled', styles.pillRed],
    later: ['Later', styles.pillBlue],
};
const MARKETING_RESULT = { ordered: ['Ordered', styles.pillGreen], interested: ['Interested', styles.pillBlue], not_interested: ['Not interested', styles.pillGrey], opted_out: ["Don't call", styles.pillRed] };
const ROLE = { agent: 'AI', member: 'Customer', system: '' };
const FILTERS = [
    ['', 'All'],
    ['order', 'Order confirmation'],
    ['marketing', 'Marketing'],
    ['support', 'Incoming (AI)'],
    ['softphone', 'Softphone'],
    ['missed', 'Missed'],
    ['sms', 'SMS'],
];
const SMS_STATUS = { sent: ['SMS sent', styles.pillBlue], failed: ['SMS failed', styles.pillRed], low_balance: ['SMS not sent: low balance', styles.pillAmber] };

/** The "we couldn't reach you" SMS sent after this call (one a day per customer). */
function SmsTag({ sms }) {
    if (!sms) return null;
    const [label, cls] = SMS_STATUS[sms.status] || [sms.status, styles.pillGrey];
    return <div style={{ marginTop: 4 }}><span className={`${styles.pill} ${cls}`} title={sms.text}>{label}</span></div>;
}

function Recording({ id, compact = false }) {
    const [url, setUrl] = useState(null);
    const [state, setState] = useState('idle');
    useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
    const load = async () => {
        try {
            setState('loading');
            const res = await myAxios.get(`call/calls/${id}/recording`, { responseType: 'blob' });
            setUrl(URL.createObjectURL(res.data));
            setState('ready');
        } catch (err) {
            setState('error');
        }
    };
    if (state === 'ready') return <audio controls autoPlay src={url} style={compact ? { width: 220, height: 32 } : { width: '100%', marginBottom: 10 }} />;
    return (
        <div style={compact ? undefined : { marginBottom: 10 }}>
            <button className={`${styles.btnSecondary} ${styles.btnSmall}`} onClick={load} disabled={state === 'loading'} title="Play the call recording">
                {state === 'loading' ? 'Loading…' : compact ? '▶ Play' : '▶ Play recording'}
            </button>
            {state === 'error' && <span className={styles.muted} style={{ marginLeft: 8 }}>{compact ? 'Not available' : 'Recording not available (kept 30 days).'}</span>}
        </div>
    );
}

export function CallDetail({ id, onClose }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const res = await myAxios.get(`call/calls/${id}`);
                setData(res.data);
            } catch (err) {
                toast.error(errMsg(err, 'Could not load the call'));
                onClose();
            }
        })();
    }, [id, onClose]);
    const c = data?.call;
    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <h3 className={styles.modalTitle}>Call {c ? `· ${c.mobile}` : ''}</h3>
                {!c ? (
                    <div className={styles.empty}>Loading…</div>
                ) : (
                    <>
                        <div className={styles.muted} style={{ marginBottom: 10 }}>
                            {fmtDateTime(c.at)} · {KIND[c.kind] || c.kind}
                            {c.campaignName && c.kind === 'marketing' ? ` · ${c.campaignName}` : ''}
                            {c.invoice ? ` · order #${c.invoice}` : ''}
                            {c.createdInvoice ? ` · new order #${c.createdInvoice}` : ''}
                            {c.agentName ? ` · ${c.handover ? 'taken over by' : 'by'} ${c.agentName}` : ''} · {dur(c.durationSec)} · {taka(c.costBdt)}
                        </div>
                        {c.summary && <div className={styles.ok}>{c.summary}</div>}
                        {c.sms && (
                            <div className={styles.muted} style={{ margin: '8px 0', padding: '8px 10px', border: '1px solid #e4e5e7', borderRadius: 8 }}>
                                <SmsTag sms={c.sms} />
                                <div style={{ marginTop: 6, color: 'inherit' }}>{c.sms.text}</div>
                                <div style={{ marginTop: 4 }}>{c.sms.at ? fmtDateTime(c.sms.at) : ''}{c.sms.costBdt ? ` · ${taka(c.sms.costBdt)} (${c.sms.parts} × 160 characters)` : ' · not charged'}</div>
                            </div>
                        )}
                        {c.recording && <Recording id={c._id} />}
                        {(data.transcript || []).length === 0 ? (
                            <div className={styles.empty}>
                                {c.kind === 'softphone'
                                    ? c.durationSec > 1
                                        ? 'The transcript appears a minute or so after the call ends — open the call again shortly.'
                                        : 'No conversation (the call was not answered).'
                                    : 'No conversation (the call was not answered).'}
                            </div>
                        ) : (
                            <div className={styles.transcript}>
                                {(() => {
                                    // softphone calls: "agent" is the shop's person; handed-over AI calls: the person after "Handed over"
                                    const person = c.agentName || 'Shop';
                                    let afterHandover = c.kind === 'softphone';
                                    return data.transcript.map((t, i) => {
                                        if (t.role === 'system') {
                                            if (/^Handed over/.test(t.text)) afterHandover = true;
                                            return <div key={i} className={styles.tSystem}>{t.text}</div>;
                                        }
                                        const label = t.role === 'agent' && afterHandover ? person : ROLE[t.role];
                                        return (
                                            <div key={i} className={t.role === 'agent' ? styles.tAgent : styles.tCustomer}>
                                                <span className={styles.tRole}>{label}</span>
                                                {t.text}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        )}
                    </>
                )}
                <div className={styles.modalActions}>
                    <button className={styles.btnSecondary} onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

function ResultCell({ c }) {
    const r = ORDER_RESULT[c.orderResult] || MARKETING_RESULT[c.marketingResult];
    return (
        <>
            {r ? <span className={`${styles.pill} ${r[1]}`}>{r[0]}</span> : <span className={styles.muted}>{c.state === 'ended' || c.outcome ? c.outcomeText : 'In progress'}</span>}
            {c.createdInvoice && <div className={styles.muted}>order #{c.createdInvoice}</div>}
            {/* the call summary is shown in the call's details (View), not here — it made rows very tall */}
        </>
    );
}

function CallHistory({ month, initialFilter = '', openCallId }) {
    const [filter, setFilter] = useState(initialFilter);
    const [list, setList] = useState({ calls: [], page: 1, pages: 1, total: 0 });
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null);
    // a notification (or link) can open one call directly: /ai-calling?tab=calls&call=<id>
    useEffect(() => {
        if (openCallId && /^[0-9a-f]{24}$/i.test(openCallId)) setOpen(openCallId);
    }, [openCallId]);

    useEffect(() => setFilter(initialFilter), [initialFilter]);

    const load = useCallback(
        async (page = 1) => {
            try {
                setLoading(true);
                const params = { page, limit: 20 };
                if (filter === 'missed') params.missed = '1';
                else if (filter === 'sms') params.sms = '1';
                else if (filter) params.kind = filter;
                const res = await myAxios.get('call/calls', { params });
                setList(res.data);
            } catch (err) {
                toast.error(errMsg(err, 'Could not load calls'));
            } finally {
                setLoading(false);
            }
        },
        [filter]
    );

    useEffect(() => { load(1); }, [load]);
    const location = useLocation();
    const navigate = useNavigate();
    const close = useCallback(() => {
        setOpen(null);
        // drop ?call= so the same notification opens the call again next time
        const params = new URLSearchParams(location.search);
        if (params.has('call')) {
            params.delete('call');
            navigate(`${location.pathname}${params.toString() ? `?${params}` : ''}`, { replace: true });
        }
    }, [location.pathname, location.search, navigate]);

    const m = month || {};
    return (
        <>
            <div className={styles.grid4}>
                {[
                    ['Calls this month', m.calls ?? 0],
                    ['Answered', m.answered ?? 0],
                    ['Orders confirmed', m.confirmed ?? 0],
                    ['Orders from marketing', m.ordered ?? 0],
                    ['Minutes', m.minutes ?? 0],
                    ['Spent', taka(m.cost)],
                ].map(([label, value]) => (
                    <div key={label} className={styles.card}>
                        <span className={styles.statLabel}>{label}</span>
                        <div className={styles.statValueSm}>{value}</div>
                    </div>
                ))}
            </div>

            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Calls</h2>
                    <button className={`${styles.btnSecondary} ${styles.btnSmall}`} onClick={() => load(list.page)} disabled={loading}>Refresh</button>
                </div>
                <div className={styles.chips} style={{ marginTop: 0 }}>
                    {FILTERS.map(([k, label]) => (
                        <button key={k || 'all'} className={`${styles.chip} ${filter === k ? styles.chipActive : ''}`} onClick={() => setFilter(k)}>
                            {label}{k === 'missed' && m.missed ? ` (${m.missed})` : ''}
                        </button>
                    ))}
                </div>
                {list.calls.length === 0 ? (
                    <div className={styles.empty}>{loading ? 'Loading…' : 'No calls yet.'}</div>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr><th>Time</th><th>Customer</th><th>Type</th><th>Order</th><th>Result</th><th>Length</th><th>Cost</th><th>Recording</th><th></th></tr>
                            </thead>
                            <tbody>
                                {list.calls.map((c) => (
                                    <tr key={c._id}>
                                        <td className={styles.num}>{fmtDateTime(c.at)}</td>
                                        <td className={styles.num}>{c.mobile}<div className={styles.muted}>{c.direction === 'inbound' ? 'called you' : 'we called'}</div></td>
                                        <td>
                                            {KIND[c.kind] || c.kind}
                                            {c.kind === 'marketing' && c.campaignName ? <div className={styles.muted}>{c.campaignName}</div> : null}
                                            {c.agentName ? <div className={styles.muted}>{c.handover ? 'AI → ' : ''}{c.agentName}</div> : null}
                                        </td>
                                        <td className={styles.num}>{c.invoice ? `#${c.invoice}` : '—'}</td>
                                        <td><ResultCell c={c} /><SmsTag sms={c.sms} /></td>
                                        <td className={styles.num}>{dur(c.durationSec)}</td>
                                        <td className={styles.num}>{taka(c.costBdt)}{c.sms?.costBdt ? <div className={styles.muted}>+ SMS {taka(c.sms.costBdt)}</div> : null}</td>
                                        <td>{c.recording ? <Recording id={c._id} compact /> : <span className={styles.muted}>—</span>}</td>
                                        <td><button className={styles.btnLink} onClick={() => setOpen(c._id)}>View</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {list.pages > 1 && (
                    <div className={styles.row} style={{ justifyContent: 'flex-end', marginTop: 12 }}>
                        <button className={`${styles.btnSecondary} ${styles.btnSmall}`} disabled={list.page <= 1 || loading} onClick={() => load(list.page - 1)}>Previous</button>
                        <span className={styles.muted}>Page {list.page} of {list.pages}</span>
                        <button className={`${styles.btnSecondary} ${styles.btnSmall}`} disabled={list.page >= list.pages || loading} onClick={() => load(list.page + 1)}>Next</button>
                    </div>
                )}
            </div>
            {open && <CallDetail id={open} onClose={close} />}
        </>
    );
}

export default CallHistory;
